import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useGoogleLogin } from "@react-oauth/google";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import {
  Dialog,
  DialogContent,
  Slide,
  Autocomplete,
  TextField,
  Grid,
  Divider,
  LinearProgress,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SignInLayout from "layouts/authentication/components/SignInLayout";
import IntroCarouselModal from "layouts/authentication/components/IntroCarouselModal";
import { useAuth } from "../../../AuthContext";
import { LanguageContext } from "../../../context/LanguageContext";
import { normalizeLang } from "../../language/langUtil";
import { registerUser, country_tel } from "../../../api/authApi";
import axiosInstance from "api/axiosInstance";
import { trackEvent } from "utils/analytics";
import { COLORS, TYPO } from "theme/tokens";
import {
  saveReferralCodeFromUrl,
  getReferralCode,
  getAuthRedirectPath,
} from "../../../utils/referral";

import "./index.css";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

export default function SignUp() {
  const [status, setStatus] = useState({ loading: false });
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneCountry: "",
    phoneNumber: "",
    referralCodeInput: "",
  });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [confirmedReading, setConfirmedReading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isReferralLocked, setIsReferralLocked] = useState(false);
  const [introOpen, setIntroOpen] = useState(false); // 註冊頁教學輪播視窗
  const navigate = useNavigate();
  const { login } = useAuth();
  const { componentLanguageData, loadLanguageFile, language, changeLanguage } =
    useContext(LanguageContext);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // 官網導流帶 ?lang=（短碼 tw/cn/en/uk 或 canonical 皆可）→ 正規化後同步全站。
    //   與 ?redirect= / ?price=（登入後導頁）是不同參數、互不影響，可同網址並存。
    const fromUrl = normalizeLang(params.get("lang"));
    if (fromUrl && fromUrl !== language) changeLanguage(fromUrl);

    saveReferralCodeFromUrl();
    const savedRef = getReferralCode();
    if (savedRef) {
      setFormData((prev) => ({ ...prev, referralCodeInput: savedRef }));
      setIsReferralLocked(true);
    }
  }, [language, changeLanguage]);

  useEffect(() => {
    loadLanguageFile("signup", "authentication/sign-up/sign-up_lang");
  }, [language, loadLanguageFile]);

  // 註冊頁教學視窗開啟判斷（同登入頁邏輯）：未關閉過、無 price/skip/no_intro 才跳
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasPrice = params.get("price");
    const skipDemo = params.get("skip_demo") || params.get("skip_intro");
    const noIntro = params.get("no_intro");
    const dismissed = localStorage.getItem("signupIntroDismissed_v1");
    if (!dismissed && !hasPrice && !skipDemo && !noIntro) {
      setIntroOpen(true);
    } else if (skipDemo) {
      localStorage.setItem("signupIntroDismissed_v1", "1");
    }
  }, []);
  const LANGUAGE = useMemo(
    () => componentLanguageData.signup || { Main: {}, Terms: {}, Error: {}, Alert: {} },
    [componentLanguageData]
  );

  // ── Google 註冊處理（access_token flow，繞過 FedCM / iframe） ──
  const handleGoogleSuccess = async (data) => {
    try {
      setStatus({ loading: true });
      const res = await axiosInstance.post("/auth/google-login", data);
      if (res.data.result && res.data.accessToken) {
        try {
          localStorage.setItem("accessToken", res.data.accessToken);
          localStorage.setItem("refreshToken", res.data.refreshToken);
        } catch (e) {}
        login(res.data.accessToken, res.data.user, "google");
        localStorage.removeItem("referralCode");
        navigate(getAuthRedirectPath());
      } else {
        toast.error(LANGUAGE.Error.Content4);
      }
    } catch (e) {
      toast.error(LANGUAGE.Error.Content4);
    } finally {
      setStatus({ loading: false });
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: (tokenResponse) => handleGoogleSuccess({ accessToken: tokenResponse.access_token }),
    onError: () => toast.error(LANGUAGE.Error.Content4),
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await country_tel();
        setCountries(res.data);
      } catch (e) {
        console.error("取得國家電話代碼失敗", e);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (status.loading) document.body.classList.add("loading");
    else document.body.classList.remove("loading");
    return () => document.body.classList.remove("loading");
  }, [status.loading]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isValidPhone = (p) => /^\d+$/.test(p);
  const passwordValid =
    /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_\-+=\[\]{};:'",.<>/?\\|`~]{8,}$/.test(
      formData.password
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (!pwd) return { score, percent: 0, label: "", color: "error" };
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    let label = LANGUAGE.Main.Content15; // 弱
    let color = "error";
    if (score >= 3) {
      label = LANGUAGE.Main.Content16; // 中
      color = "warning";
    }
    if (score === 4) {
      label = LANGUAGE.Main.Content17; // 強
      color = "success";
    }
    return { score, percent: (score / 4) * 100, label, color };
  };

  const submitRegister = async () => {
    try {
      setStatus({ loading: true });
      await registerUser(formData);
      trackEvent("sign_up", { method: "password" });
      toast.success(LANGUAGE.Alert.Content1);
      try {
        localStorage.setItem("pendingVerifyEmail", formData.email);
        localStorage.removeItem("referralCode");
      } catch (err) {
        console.warn("localStorage 寫入失敗：", err);
      }
      navigate("/authentication/verify-email");
    } catch (error) {
      let msg = "";
      const code = error.response?.data?.message;
      if (code === "Error_register_001") msg = LANGUAGE.Error.Content15;
      else if (code === "Error_register_002") msg = LANGUAGE.Error.Content2;
      else if (code === "Error_register_003") msg = LANGUAGE.Error.Content16;
      else if (code === "Error_register_004") msg = LANGUAGE.Error.Content20;
      else if (code === "Error_rateLimit_001") msg = LANGUAGE.Error.Content18;
      toast.error(msg || LANGUAGE.Error.Content4);
      setStatus({ loading: false });
    }
  };

  const handleRegisterClick = () => {
    if (status.loading) return;

    if (!formData.name) {
      toast.error(LANGUAGE.Main.Content30); // 請輸入您的姓名
      return;
    }
    if (!isValidEmail(formData.email)) {
      toast.error(LANGUAGE.Error.Content1);
      return;
    }
    if (!formData.phoneCountry) {
      toast.error(LANGUAGE.Error.Content19);
      return;
    }
    if (!isValidPhone(formData.phoneNumber)) {
      toast.error(LANGUAGE.Error.Content17);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error(LANGUAGE.Error.Content13);
      return;
    }
    if (!passwordValid) {
      toast.error(LANGUAGE.Error.Content14);
      return;
    }

    // 還沒同意條款 → 開 modal
    if (!agreedToTerms) {
      setShowTermsModal(true);
      return;
    }
    submitRegister();
  };

  const handleAgreeTerms = () => {
    setAgreedToTerms(true);
    setShowTermsModal(false);
    // 同意後直接 submit（已通過上面 validation）
    submitRegister();
  };

  const handleConfirmReadingClick = () => {
    if (!hasScrolledToBottom) {
      toast.error(LANGUAGE.Alert.Content4); // 請閱讀完條款後才能勾選同意
      return;
    }
    setConfirmedReading((v) => !v);
  };

  // 灰底 filled input 共用樣式 — bg #F6F6F6 / radius 6px / 內文 bodyM #000 / placeholder bodyM #C4C4C4
  const filledInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.grayLightest,
      borderRadius: "6px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused": {
        backgroundColor: COLORS.white,
        "& fieldset": { border: "1px solid", borderColor: COLORS.main },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 8px 8px 8px",
      ...TYPO.bodyM,
      color: COLORS.black,
      "&::placeholder": { ...TYPO.bodyM, color: COLORS.grayMedian, opacity: 1 },
    },
  };

  const cardLogo =
    language === "zh-TW" || language === "zh-CN"
      ? "/images/logo/logo_2_1.png"
      : "/images/logo/logo_2_2.png";

  const strength = getPasswordStrength(formData.password);

  return (
    <SignInLayout>
      <IntroCarouselModal
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        mode="signup"
        dismissKey="signupIntroDismissed_v1"
      />
      <Card
        sx={{
          width: "100%",
          maxWidth: 920,
          height: { xs: "auto", md: 620 },
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container sx={{ height: "inherit" }}>
          {/* === 左欄：品牌（白底 + 右框線 #E6E6E6 + logo 162×254 置中）=== */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.white,
              borderRight: `1px solid ${COLORS.grayLighter}`,
              px: 8,
              py: 6,
            }}
          >
            <MDBox
              component="img"
              src={cardLogo}
              alt="MASQuant"
              sx={{ width: 162, height: 254, objectFit: "contain" }}
            />
          </Grid>

          {/* === 右欄：表單（白底；置中；內容太長 → 框內下拉 scroll）=== */}
          <Grid item xs={12} md={7} sx={{ display: "flex", justifyContent: "center", background: COLORS.white }}>
            <MDBox
              sx={{
                width: "100%",
                maxWidth: 510,
                maxHeight: { md: 620 },
                overflowY: "auto",
                px: { xs: 3, sm: 4, md: "19px" },
                py: { xs: 3, md: 5 },
              }}
            >
              <MDBox
                display={{ xs: "flex", md: "none" }}
                justifyContent="center"
                mb={3}
              >
                <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ maxHeight: 120, objectFit: "contain" }} />
              </MDBox>

              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500, mb: 0.5 }}>
                {LANGUAGE.Main.Content25 /* 建立您的帳號 */}
              </MDTypography>
              <MDTypography sx={{ ...TYPO.title, color: COLORS.grayDark, fontWeight: 500, mb: 3 }}>
                {LANGUAGE.Main.Content26 /* 加入 MASQuant */}
              </MDTypography>

              {/* Google 註冊：useGoogleLogin hook (access_token flow, popup)
                   完全繞過 GoogleLogin 元件的 iframe widget，
                   不再受 FedCM / /gsi/status 403 影響 */}
              <MDBox mb={2}>
                <MDBox
                  onClick={() => googleLogin()}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "8px",
                    border: "1px solid #fcd9b6",
                    bgcolor: COLORS.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                    "&:hover": { borderColor: COLORS.main },
                  }}
                >
                  <img
                    src="/images/Google.svg"
                    alt="Google"
                    style={{ width: 24, height: 24 }}
                  />
                </MDBox>
              </MDBox>

              {/* 已經有帳號了？ → 前往登入（兩行） */}
              <MDBox display="flex" flexDirection="column" gap={0.5} mb={3}>
                <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black, fontWeight: 400 }}>
                  {LANGUAGE.Main.Content27 /* 已經有帳號了？ */}
                </MDTypography>
                <Link
                  to="/authentication/sign-in"
                  className="auth-underline"
                  style={{ ...TYPO.title, color: COLORS.main, fontWeight: 500, textDecoration: "none" }}
                >
                  {`→ ${LANGUAGE.Main.Content28 /* 前往登入 */}`}
                </Link>
              </MDBox>

              {/* 或使用 Email 註冊 分隔線 */}
              <MDBox display="flex" alignItems="center" mb={3}>
                <Divider sx={{ flex: 1, borderColor: COLORS.grayLighter }} />
                <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black, fontWeight: 400, px: 1.5, whiteSpace: "nowrap" }}>
                  {LANGUAGE.Main.Content29 /* 或使用 Email 註冊 */}
                </MDTypography>
                <Divider sx={{ flex: 1, borderColor: COLORS.grayLighter }} />
              </MDBox>

              {/* 表單欄位 */}
              <MDBox component="form" autoComplete="off">
                {/* 姓名 */}
                <MDBox mb={2}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}
                  >
                    {LANGUAGE.Main.Content1 /* 姓名 */}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={LANGUAGE.Main.Content30}
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* 手機號碼 = 國碼 + 號碼 */}
                <MDBox mb={2}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}
                  >
                    {LANGUAGE.Main.Content20 /* 手機號碼 */}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <Autocomplete
                    options={countries}
                    value={selectedCountry}
                    onChange={(_, v) => {
                      setSelectedCountry(v);
                      setFormData((p) => ({
                        ...p,
                        phoneCountry: v?.phoneNumber?.toString() || "",
                      }));
                    }}
                    getOptionLabel={(o) =>
                      typeof o === "object" && o?.country
                        ? `${o.country} (${o.phoneNumber ? "+" + o.phoneNumber : ""})`
                        : ""
                    }
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={LANGUAGE.Main.Content18}
                        fullWidth
                        sx={filledInputSx}
                      />
                    )}
                    sx={{ mb: 1.5 }}
                  />
                  <MDInput
                    type="tel"
                    inputMode="numeric"
                    placeholder={LANGUAGE.Main.Content32}
                    fullWidth
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        phoneNumber: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* Email */}
                <MDBox mb={2}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}
                  >
                    Email<span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="email"
                    placeholder={LANGUAGE.Main.Content31}
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* 密碼 */}
                <MDBox mb={2}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}
                  >
                    {LANGUAGE.Main.Content3 /* 密碼 */}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    placeholder={LANGUAGE.Main.Content33}
                    fullWidth
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    sx={filledInputSx}
                    InputProps={
                      formData.password.length > 0
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword((v) => !v)}
                                  edge="end"
                                  size="small"
                                  tabIndex={-1}
                                >
                                  <Icon fontSize="small">
                                    {showPassword ? "visibility" : "visibility_off"}
                                  </Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                  {/* 強度條（有輸入才顯示） */}
                  {formData.password.length > 0 && (
                    <MDBox mt={1}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <MDTypography variant="caption" color="dark">
                          {LANGUAGE.Main.Content35 /* 強度 */}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color={strength.color}
                          fontWeight="medium"
                        >
                          {strength.label}
                        </MDTypography>
                      </MDBox>
                      <LinearProgress
                        variant="determinate"
                        value={strength.percent}
                        color={strength.color}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: COLORS.lighter,
                        }}
                      />
                    </MDBox>
                  )}
                </MDBox>

                {/* 確認密碼 */}
                <MDBox mb={2}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}
                  >
                    {LANGUAGE.Main.Content11 /* 確認密碼 */}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={LANGUAGE.Main.Content34}
                    fullWidth
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    sx={filledInputSx}
                    InputProps={
                      formData.confirmPassword.length > 0
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowConfirmPassword((v) => !v)
                                  }
                                  edge="end"
                                  size="small"
                                  tabIndex={-1}
                                >
                                  <Icon fontSize="small">
                                    {showConfirmPassword
                                      ? "visibility"
                                      : "visibility_off"}
                                  </Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                </MDBox>

                {/* 推薦碼 */}
                <MDBox mb={3}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}
                  >
                    {LANGUAGE.Main.Content36 /* 推薦碼 */}
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={LANGUAGE.Main.Content24}
                    fullWidth
                    name="referralCodeInput"
                    value={formData.referralCodeInput || ""}
                    onChange={handleChange}
                    sx={filledInputSx}
                    InputProps={{ readOnly: isReferralLocked }}
                  />
                </MDBox>

                {/* 註冊 */}
                <MDButton
                  onClick={handleRegisterClick}
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={status.loading}
                  sx={{
                    height: 60,
                    borderRadius: "16px",
                    background: COLORS.gradient,
                    boxShadow: "4px 4px 9px 0 rgba(222, 86, 15, 0.20)",
                    textTransform: "none",
                    ...TYPO.title,
                    color: `${COLORS.white} !important`,
                    fontWeight: 500,
                    "&:hover": {
                      background: COLORS.gradient,
                      boxShadow: "4px 4px 12px 0 rgba(222, 86, 15, 0.30)",
                    },
                    "&:disabled": {
                      background: COLORS.gradient,
                      color: `${COLORS.white} !important`,
                      opacity: 0.6,
                    },
                  }}
                >
                  {LANGUAGE.Main.Content4 /* 註冊 */}
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {/* === 條款 Modal === */}
      <Dialog
        open={showTermsModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setShowTermsModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {/* Title + 關閉 */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
        >
          <MDBox
            component="h2"
            sx={{
              margin: 0,
              color: COLORS.black,
              fontFamily: '"Outfit", var(--cjk-font, "Noto Sans TC"), sans-serif',
              fontSize: "20px",
              fontWeight: 600,
              lineHeight: "30px",
            }}
          >
            {LANGUAGE.Main.Content37 /* 會員權益條款 */}
          </MDBox>
          <IconButton onClick={() => setShowTermsModal(false)} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>

        <DialogContent
          dividers
          sx={{ maxHeight: 460, overflowY: "auto", px: 3, py: 2 }}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollTop + clientHeight >= scrollHeight - 10)
              setHasScrolledToBottom(true);
          }}
        >
          <MDBox
            sx={{
              whiteSpace: "pre-wrap",
              color: COLORS.black,
              fontFamily: '"Outfit", var(--cjk-font, "Noto Sans TC"), sans-serif',
              fontSize: "12.5px",
              fontWeight: 400,
              lineHeight: "1.7",
            }}
          >
            {/* 2026-05-25 去掉每行開頭縮排（lang template literal 的程式碼縮排），靠左對齊 */}
            {(LANGUAGE.Terms.Content || "")
              .split("\n")
              .map((line) => line.replace(/^[\s　]+/, ""))
              .join("\n")}
          </MDBox>
        </DialogContent>

        {/* Footer：左 checkbox + 確認閱讀文字 / 右 我同意鈕 */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          gap={2}
        >
          <MDBox
            onClick={handleConfirmReadingClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {/* 自訂 checkbox：未勾灰框、勾選橘底白勾，對齊設計系統 */}
            <MDBox
              sx={{
                width: 16,
                height: 16,
                borderRadius: "3px",
                border: "1.5px solid",
                borderColor: confirmedReading ? "info.main" : "grey.400",
                backgroundColor: confirmedReading ? "info.main" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all 0.15s",
              }}
            >
              {confirmedReading && (
                <Icon sx={{ color: COLORS.white, fontSize: "14px !important" }}>
                  check
                </Icon>
              )}
            </MDBox>
            <MDBox
              component="span"
              sx={{
                color: "info.main",
                fontSize: 13,
                fontWeight: 500,
                "&:hover": { color: "info.focus" },
              }}
            >
              {LANGUAGE.Terms?.ConfirmText /* 我已完整閱讀並同意上述條款 */}
            </MDBox>
          </MDBox>
          <MDButton
            onClick={handleAgreeTerms}
            disabled={!confirmedReading}
            variant="gradient"
            color="info"
            sx={{
              borderRadius: 2,
              minWidth: 100,
              background: confirmedReading
                ? "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)"
                : COLORS.lighter,
              color: confirmedReading ? COLORS.white : COLORS.white,
              "&:hover": {
                background: confirmedReading
                  ? "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)"
                  : COLORS.lighter,
              },
              "&:disabled": {
                background: COLORS.lighter,
                color: COLORS.white,
              },
            }}
          >
            {LANGUAGE.Main.Content38 /* 我同意 */}
          </MDButton>
        </MDBox>
      </Dialog>

      {status.loading && (
        <div className="loading-overlay">
          <CircularProgress color="inherit" />
        </div>
      )}
    </SignInLayout>
  );
}
